import type { TmsInvoiceCreateRow, TmsWriteRow } from '@numeo/types-main-web-app'
import { Badge } from '@/components/ds2'
import { DocumentChips } from './DocumentChips'
import { LOAD_STAGE_LABEL } from './format'
import { LoadFixActions } from './LoadFixActions'
import { byFixOrder, documentActions, REASON_LABEL, reasonActions } from './rowActions'

type BadgeType = 'success' | 'default' | 'warning' | 'danger'

const OUTCOME_BADGE: Record<TmsWriteRow['outcome'], { type: BadgeType; label: string }> = {
    updated: { type: 'success', label: 'Done' },
    unchanged: { type: 'default', label: 'Already so' },
    skipped: { type: 'warning', label: 'Skipped' },
    refused: { type: 'danger', label: 'Refused' },
}

/** The invoice card's words for its outcomes: a load it did not invoice is waiting on a fix, not refused for good. */
function badgeOf(card: string, row: TmsInvoiceCreateRow): { type: BadgeType; label: string } {
    if (card === 'tms_invoices.create') {
        if (row.outcome === 'updated') return { type: 'success', label: 'Invoiced' }
        if (row.code === 'already_invoiced') return { type: 'default', label: 'Already invoiced' }
        if (row.code === 'not_ready') return { type: 'warning', label: 'Not invoiced' }
    }
    return OUTCOME_BADGE[row.outcome] ?? { type: 'default', label: row.outcome }
}

/** A status change the card made, in the board's words ("In transit → Delivered"), for a row that carries no message of its own. */
const stageChange = (row: TmsWriteRow): string | undefined =>
    row.from_stage && row.to_stage && row.from_stage !== row.to_stage ? `${LOAD_STAGE_LABEL[row.from_stage]} → ${LOAD_STAGE_LABEL[row.to_stage]}` : undefined

/** A reason's message as one clause of the row's line. */
const clause = (message: string) => message.trim().replace(/\.$/, '')

interface WriteRowsProps {
    /** The write card that answered, e.g. `tms_invoices.create`. */
    card: string
    rows: TmsWriteRow[]
    onSend: (message: string) => void
}

/**
 * A write card's answer: what happened to each load, in the card's words. A load the invoice card left out shows what it is
 * waiting on and the fixes, right here (upload its rate con or POD, set its rate), so "invoice these loads" never ends at a refusal.
 * A load just marked delivered shows its documents and the uploads it still needs, the next step before its invoice.
 */
export const WriteRows = ({ card, rows, onSend }: WriteRowsProps) => (
    <ul className="flex flex-col gap-1.5 text-xs">
        {rows.map((plain) => {
            const row = plain as TmsInvoiceCreateRow
            const badge = badgeOf(card, row)
            // In the order they are fixed, as the actions under them.
            const reasons = row.outcome === 'refused' ? byFixOrder(row.reasons ?? []) : []
            const delivered = row.outcome === 'updated' ? row.document_chips : undefined
            const fixes = reasons.length ? reasonActions(reasons) : documentActions(delivered)
            return (
                <li key={row.load_id} className="flex flex-col gap-1.5 border-t border-border pt-1.5 first:border-t-0 first:pt-0">
                    <div className="flex flex-wrap items-start gap-2">
                        <span className="font-medium">#{row.load_number}</span>
                        <Badge variant="soft" type={badge.type} size="sm" label={badge.label} />
                        <span className="text-foreground-muted">
                            {reasons.length
                                ? reasons.map((reason) => REASON_LABEL[reason.code] ?? clause(reason.message)).join(' · ')
                                : (row.message ?? stageChange(row))}
                        </span>
                        {delivered && <DocumentChips documents={delivered} />}
                    </div>
                    {fixes.length > 0 && <LoadFixActions loadNumber={row.load_number} loadId={row.load_id} actions={fixes} onSend={onSend} />}
                </li>
            )
        })}
    </ul>
)
